'use client'
import { FadeIn, FadeInStagger } from '@/components/ui/animation/faideIn'
import { buttonVariants } from '@/components/ui/button'
import { SvgCryptoCurrency } from '@/components/ui/icons'
import { getApplyRedirectUrl } from '@/lib/utils/functions'
import { cn } from '@/lib/utils/helpers'
import { useAuth } from '@/lib/utils/providers/supabase/supabase-auth-provider'
import { ArrowRightIcon } from '@heroicons/react/20/solid'
import Link from 'next/link'
import { FiArrowUpRight } from 'react-icons/fi'
import MentorMatchingCard from '../aichat/ChatPage'
import { PoweredBy } from '../driveChange/PoweredBy'

const MentorsHero = () => {
  const { session } = useAuth()
  const mentorsLogo = [
    {
      img: '/IBM_logo.svg',
      alt: 'IBM',
    },
    {
      img: '/EY_logo.svg',
      alt: 'EY',
    },
    {
      img: '/hsbc_logo.png',
      alt: 'HSBC',
    },
    {
      img: '/Microsoft_logo.svg',
      alt: 'Microsoft',
    },
    {
      img: '/deloitte_logo.png',
      alt: 'deloitte',
    },
  ]
  return (
    <div className="flex flex-col align-center gap-4 w-screen bg-gradient-to-r from-gray-900 to-black">
      <div className="relative isolate z-0">
        <svg
          className="absolute inset-x-0   top-0 xl:left-32 -z-10 h-[50rem] w-full stroke-gray-800 [mask-image:radial-gradient(32rem_32rem_at_center,white,transparent)]"
          aria-hidden="true"
        >
          <defs>
            <pattern
              id="1f932ae7-37de-4c0a-a8b0-a6e3b4d44b84"
              width={200}
              height={200}
              x="50%"
              y={-1}
              patternUnits="userSpaceOnUse"
            >
              <path d="M.5 200V.5H200" fill="none" />
            </pattern>
          </defs>
          <svg x="50%" y={-1} className="overflow-visible fill-gray-800">
            <path
              d="M-200 0h201v201h-201Z M600 0h201v201h-201Z M-400 600h201v201h-201Z M200 800h201v201h-201Z"
              strokeWidth={0}
            />
          </svg>
          <rect
            width="100%"
            height="100%"
            strokeWidth={0}
            fill="url(#1f932ae7-37de-4c0a-a8b0-a6e3b4d44b84)"
          />
        </svg>
        <div
          className="absolute left-1/2 right-0 top-0 -z-10 -ml-24 transform-gpu overflow-hidden blur-3xl lg:ml-24 xl:ml-48"
          aria-hidden="true"
        ></div>
        <div className="overflow-hidden mt-[-170px]">
          <FadeInStagger
            faster
            className="mx-auto max-w-7xl px-6 pb-10 pt-36 sm:pt-60 lg:px-8 lg:pt-32"
          >
            <div className="flex justify-center mt-[120px]">
              <FadeIn className="bg-white bg-opacity-5 flex gap-1 px-0.5  rounded-full py-1  border border-[#424242] xl:h-8 ">
                <button
                  className="leading-[20px]   bg-white bg-opacity-10 px-1 lg:px-3 py-0.5 rounded-full cursor-default
                text-center text-white text-sm font-medium"
                >
                  Inspire Future Leaders
                </button>
                <div className="flex flex-row items-center">
                  <Link
                    href={`${getApplyRedirectUrl(session)}`}
                    className=" cursor-pointer  text-white  px-2  flex items-center gap-1 text-sm font-medium  leading-[20px]"
                  >
                    Apply <ArrowRightIcon className="w-4 h-4 " />
                  </Link>
                </div>
              </FadeIn>
            </div>
            <FadeIn className="mt-2">
              <h1 className="text-5xl lg:text-[72px] font-bold lg:leading-[90px] text-center text-white">
                Elite Mentors Forging the
                <br />{' '}
                <p className=" text-transparent bg-clip-text bg-gradient-to-r from-[#3538CD] to-[#2E90FA]">
                  Future of AI Leadership
                </p>
              </h1>
            </FadeIn>
            <FadeIn className="text-[#98A2B3] text-[20px] leading-[30px] font-normal text-center mt-8 max-w-3xl mx-auto">
              Connecting Ambitious Founders with Industry Experts Through
              Rigorous Challenges
            </FadeIn>
            <FadeIn className="flex justify-center gap-4 mt-12">
              <Link
                href="/i/profile"
                className={cn(
                  buttonVariants({ variant: 'secondary' }),
                  ' gap-3 w-[300px]  h-[60px] px-5 py-4',
                  'border-[#155EEF]',
                )}
                style={{ lineHeight: '28px' }}
              >
                Get Started as a Mentor
                <FiArrowUpRight className="text-white text-2xl leading-7 ml-3.5" />
              </Link>
            </FadeIn>
          </FadeInStagger>
        </div>
      </div>
      <FadeInStagger className="px-6 sm:px-0" faster>
        <MentorMatchingCard
          videoUrl={'https://youtu.be/LuXSGIWp2ug'}
          bg={'/images/home/mentors/pic-4.webp'}
        />
      </FadeInStagger>

      <div className="bg-gradient-to-t from-[#000] pt-16 sm:pt-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <PoweredBy
            title={'Our Mentors Work At:'}
            images={[
              ...mentorsLogo.map((el) => el.img),
              '/featherdev.webp',
              '/globalbank.webp',
              '/spherule.webp',
              '/blockdojo.webp',
              '/vaionex.webp',
            ]}
          />
          <FadeInStagger faster className="pb-16">
            <FadeIn className="w-12 mx-auto">
              <SvgCryptoCurrency />
            </FadeIn>
            <div className="max-w-3xl mx-auto pt-8">
              <FadeIn className="text-center text-[#155EEF] text-base font-semibold leading-normal">
                Be a Catalyst for Change
              </FadeIn>
              <FadeIn className="pt-3 text-center text-white text-[36px] font-semibold leading-[44px]">
                Mentor AI&apos;s Next Innovators
              </FadeIn>
              <FadeIn className="pt-5 text-center text-[#98A2B3] text-xl font-normal leading-[30px]">
                Join AI Forge as a mentor and shape the future of AI innovation.
                Inspire, guide, and empower the next generation of AI pioneers
              </FadeIn>
            </div>
          </FadeInStagger>
        </div>
      </div>
    </div>
  )
}

export default MentorsHero
